import React from 'react';

import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';

import SearchIcon from '@material-ui/icons/Search';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import { toast } from 'react-toastify';

import api from '../../ApiService';
import { CodeGroupMappingsFilter } from '../../helper';

export default class CodeGroupMappings extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isDirty: false,
            statusFilter: CodeGroupMappingsFilter.NotMapped,
            hospitalRevenueCategoryFilter: null,
            hospitalCodeDescriptionFilter: null,
            loadingMappingValidation: true,
            loadingMappingDetails: true,
            mappingsVM: {
                mappings: [],
                hospitalRevenueCategories: [],
                iVetCodeGroups: [],
                manufacturers: [],
                unitsOfMeasure: [],
                maxCodeGroupMappingQuantity: 0
            },
            mappingValidationVM: [],
            okButtonValue: 'Save'
        };

        this.additionalFieldsEditor = this.additionalFieldsEditor.bind(this);
        this.codeGroupEditor = this.codeGroupEditor.bind(this);
        this.quantityEditor = this.quantityEditor.bind(this);
        this.manufacturerEditor = this.manufacturerEditor.bind(this);
        this.unitOfMeasureEditor = this.unitOfMeasureEditor.bind(this);
    }

    componentDidMount = async () => {
        this.props.alertCodeGroupMappingsComponentOfHospitalChange(this.alertCodeGroupMappingsComponentOfHospitalChange);

        this.setState(
            { selectedHospital: this.props.selectedHospital },
            () => {
                this.loadMappings();
                this.loadMappingValidation();
            }
        );
    }

    getEmptyMappingsVM = () => {
        return {
            mappings: [],
            hospitalRevenueCategories: [],
            iVetCodeGroups: [],
            manufacturers: [],
            unitsOfMeasure: []
        };
    }

    loadMappings = async () => {
        this.setState({
            loadingMappingDetails: true,
            mappingsVM: this.getEmptyMappingsVM()
        });

        let mappingsVM = await api.getCodeGroupMappings(
            this.state.selectedHospital.nameHash,
            this.state.statusFilter,
            this.state.hospitalRevenueCategoryFilter,
            this.state.hospitalCodeDescriptionFilter
        );
        if (mappingsVM) {
            // Note: API GET can return many new mapping records not yet in DB. Since much of the UI depends on a unique ID, generate
            // a unique, negtive ID for each row where ID === 0. Negative IDs will be converted back to 0 before submit.
            mappingsVM.mappings.forEach((mapping, index) => {
                if (!mapping.mapping.id)
                    mappingsVM.mappings[index].mapping.id = index * -1;
            });

            if (this.state.statusFilter === CodeGroupMappingsFilter.NeedsReview) {
                this.setState({
                    isDirty: false,
                    okButtonValue: 'Confirm',
                    loadingMappingDetails: false,
                    mappingsVM: mappingsVM
                });
            } else {
                this.setState({
                    isDirty: false,
                    okButtonValue: 'Save',
                    loadingMappingDetails: false,
                    mappingsVM: mappingsVM
                });
            }
        }
    }

    loadMappingValidation = async () => {
        this.setState({ loadingMappingValidation: true, validationVM: null });

        let mappingValidationVM = await api.getCodeGroupMappingValidation(this.state.selectedHospital.nameHash);
        if (mappingValidationVM) {
            this.setState({
                loadingMappingValidation: false, mappingValidationVM: mappingValidationVM
            });
        }
    }

    alertCodeGroupMappingsComponentOfHospitalChange = (hospital) => {
        this.setState(
            { selectedHospital: hospital },
            () => {
                this.loadMappings();
                this.loadMappingValidation();
            }
        );
    }

    formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    }

    onClickCancel = async () => {
        await this.loadMappings();
        toast.success("Changes canceled.");
    }

    onClickSave = async () => {
        // Grab only dirty mappings.
        let mappingsToSave = [];

        if (this.state.statusFilter === CodeGroupMappingsFilter.NeedsReview) {
            this.state.mappingsVM.mappings.slice().forEach((value) => {
                // Validating/Confirming the mappings
                let mapping = value.mapping;
                mapping.isConfirmed = true;
                mappingsToSave.push(mapping);
            });
        } else {
            this.state.mappingsVM.mappings.slice().forEach((value) => {
                if (value.isDirty) {
                    // Clean up ID's for save.  If negative, this is a new record.
                    let mapping = value.mapping;
                    mapping.id = mapping.id > 0 ? mapping.id : 0;
                    mappingsToSave.push(mapping);
                }
            });
        }

        let success = await api.saveCodeGroupMappings(mappingsToSave);
        if (success) {
            // Success. Clear dirty state & reload.
            this.loadMappings();
            this.loadMappingValidation();
            toast.success("Mappings updated.");
        }
    }

    codeGroupTemplate = (id) => {
        let name = "(Not Mapped)";

        if (id !== undefined && id) {
            let codeGroup = this.state.mappingsVM.iVetCodeGroups.find((val) => { return val.id === id; });
            if (codeGroup && codeGroup.name) {
                name = codeGroup.name;
            }
        }

        return <span>{name}</span>;
    }

    handleFilterChange = async (e, field) => {
        this.setState({ [field]: e.target.value }, this.loadMappings);
    }

    handleSelectChange = (mappingId, field, value) => {
        // Find record using ID.  DataTable-supplied rowIndex may not correlate to index in state. (esp. when sorting)
        let mappings = this.state.mappingsVM.mappings.slice();
        let rowIndex = mappings.findIndex((value) => value.mapping.id === mappingId);

        mappings[rowIndex].isDirty = true;
        mappings[rowIndex].mapping = {
            ...mappings[rowIndex].mapping,
            [field]: value
        };

        this.setState({
            isDirty: true,
            mappingsVM: {
                ...this.state.mappingsVM,
                mappings: mappings
            }
        });
    }

    handleMappingSelectChange = (mappingId, value) => {
        // Find record using ID.  DataTable-supplied rowIndex may not correlate to index in state. (esp. when sorting)
        let mappings = this.state.mappingsVM.mappings.slice();
        let rowIndex = mappings.findIndex((mappingValue) => mappingValue.mapping.id === mappingId);

        // Auto-set confirm status.
        let isConfirmed = value ? true : false;

        mappings[rowIndex].isDirty = true;
        mappings[rowIndex].mapping = {
            ...mappings[rowIndex].mapping,
            iVetCodeGroupId: value,
            isConfirmed
        };

        // Update.
        this.setState({
            isDirty: true,
            mappingsVM: {
                ...this.state.mappingsVM,
                mappings: mappings
            }
        });
    }

    setIsConfirmed = (mappingId, isConfirmed) => {
        // Find record using ID.  DataTable-supplied rowIndex may not correlate to index in state. (esp. when sorting)
        let mappings = this.state.mappingsVM.mappings.slice();
        let rowIndex = mappings.findIndex((value) => value.mapping.id === mappingId);

        mappings[rowIndex] = {
            ...mappings[rowIndex],
            isDirty: true,
            mapping: {
                ...mappings[rowIndex].mapping,
                isConfirmed
            }
        };

        this.setState({
            isDirty: true,
            mappingsVM: {
                ...this.state.mappingsVM,
                mappings: mappings
            }
        });
    }

    codeGroupEditor = (rowData, rowProps) => {
        let key = `iVetCodeGroupId_${rowProps.rowIndex}`;
        let value = 0;
        if (rowData.mapping.iVetCodeGroupId) {
            value = rowData.mapping.iVetCodeGroupId;
        }

        return (
            <Select disableUnderline
                style={{ width: '100%', maxWidth: '100%' }}
                value={value}
                onChange={(event) => this.handleMappingSelectChange(rowData.mapping.id, event.target.value)}
                inputProps={{ name: key, id: key, key: key }}
            >
                <MenuItem key={0} value={0}>(Not Mapped)</MenuItem>
                {this.state.mappingsVM.iVetCodeGroups.map((codeGroup, i) => {
                    return <MenuItem key={i + 1} value={codeGroup.id}>{codeGroup.name}</MenuItem>;
                })}
            </Select>
        );
    }

    manufacturerEditor = (rowData, rowProps) => {
        let key = `manufacturerId_${rowProps.rowIndex}`;
        let value = 0;
        if (rowData.mapping.manufacturerId) {
            value = rowData.mapping.manufacturerId;
        }

        return (
            <FormControl>
                <InputLabel>Manufacturer</InputLabel>
                <Select disableUnderline
                    label={"Manufacturer"}
                    style={{ width: '100%', maxWidth: '100%' }}
                    value={value}
                    onChange={(event) => this.handleSelectChange(rowData.mapping.id, 'manufacturerId', event.target.value)}
                    inputProps={{ name: key, id: key, key: key }}
                >
                    <MenuItem key={0} value={0}>(Not Selected)</MenuItem>
                    {this.state.mappingsVM.manufacturers.map((manufacturer, i) => {
                        return <MenuItem key={i + 1} value={manufacturer.id}>{manufacturer.name}</MenuItem>;
                    })}
                </Select>
            </FormControl>
        );
    }

    quantityEditor = (rowData, rowProps) => {
        let key = `quantity_${rowProps.rowIndex}`;
        let value = 0;
        if (rowData.mapping.quantity && rowData.mapping.quantity <= this.state.mappingsVM.maxCodeGroupMappingQuantity) {
            value = rowData.mapping.quantity;
        }

        // Customer requested a dropdown, so enumerate a list from maxCodeGroupMappingQuantity provided in the app settings.
        let quantityOptions = [...Array(this.state.mappingsVM.maxCodeGroupMappingQuantity).keys()].map(i => ++i);
        return (
            <FormControl>
                <InputLabel>Quantity</InputLabel>
                <Select disableUnderline
                    style={{ width: '100%', maxWidth: '100%' }}
                    value={value}
                    onChange={(event) => this.handleSelectChange(rowData.mapping.id, 'quantity', event.target.value)}
                    inputProps={{ name: key, id: key, key: key }}
                >
                    <MenuItem key={0} value={0}>(Not Selected)</MenuItem>
                    {quantityOptions.map((qty, i) => {
                        return <MenuItem key={i + 1} value={qty}>{qty}</MenuItem>;
                    })}
                </Select>
            </FormControl>
        );
    }

    unitOfMeasureEditor = (rowData, rowProps) => {
        let key = `unitOfMeasureId_${rowProps.rowIndex}`;
        let value = 0;
        if (rowData.mapping.unitOfMeasureId) {
            value = rowData.mapping.unitOfMeasureId;
        }

        return (
            <FormControl>
                <InputLabel>UOM</InputLabel>
                <Select disableUnderline
                    style={{ width: '100%', maxWidth: '100%' }}
                    value={value}
                    onChange={(event) => this.handleSelectChange(rowData.mapping.id, 'unitOfMeasureId', event.target.value)}
                    inputProps={{ name: key, id: key, key: key }}
                >
                    <MenuItem key={0} value={0}>(Not Selected)</MenuItem>
                    {this.state.mappingsVM.unitsOfMeasure.map((unit, i) => {
                        return <MenuItem key={i + 1} value={unit.id}>{unit.name}</MenuItem>;
                    })}
                </Select>
            </FormControl>
        );
    }

    additionalFieldsEditor = (rowData, rowProps) => {
        // Look up matching code group.
        if (!rowData.mapping.iVetCodeGroupId) return null;
        let codeGroup = this.state.mappingsVM.iVetCodeGroups.find(
            (value) => value.id === rowData.mapping.iVetCodeGroupId
        );

        // If no fields required, don't show.
        if (!codeGroup || !codeGroup.requireProductAttributes) return null;

        return (
            <div>
                {this.manufacturerEditor(rowData, rowProps)}
                <div style={{display: 'flex'}}>
                    {this.quantityEditor(rowData, rowProps)}
                    {this.unitOfMeasureEditor(rowData, rowProps)}
                </div>
            </div>
        );
    }

    renderMappingValidationTable = () => {
        if (this.state.loadingMappingValidation)
            return <em>Loading...</em>;

        let items = this.state.mappingValidationVM.validationRows;

        // 1 column for 0-7, 2 columns for 8-14, otherwise 3.
        let rowsPerColumn = 7;
        if (items.length > 7 && items.length < 15)
            rowsPerColumn = Math.ceil(items.length / 2);
        else if (items.length >= 15)
            rowsPerColumn = Math.ceil(items.length / 3);

        let columns = [];
        let columnIndex = -1;

        // Build columns.
        items.forEach((item, index) => {
            if (index % rowsPerColumn === 0) {
                columnIndex++;
                columns.push([]);
            }

            columns[columnIndex].push(item);
        });

        return (
            <div style={{ display: 'flex' }}>
                <Grid container spacing={24}>
                    {columns.map((col, index) => (
                        <Grid item xs={4} key={index}>
                            <Table>
                                <TableHead>
                                    <TableRow className="validation-table-highlight">
                                        <TableCell component="th" style={{ textAlign: 'center', fontWeight: '500', fontSize: 'unset', color: 'black' }}>Mapped Code</TableCell>
                                        <TableCell component="th" style={{ textAlign: 'right', fontWeight: '500', fontSize: 'unset', color: 'black', width: '8em' }}># Mapped</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {col.map((item) => (
                                        <TableRow key={item.codeGroupName}>
                                            <TableCell component="th" scope="row" style={{ fontWeight: '500', fontSize: 'unset' }}>{item.codeGroupName}</TableCell>
                                            <TableCell style={{ textAlign: 'right', width: '8em' }}>{item.countMappings}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Grid>
                    ))}
                </Grid>
            </div>
        );
    }

    currencyTemplate = (rowData, column) => {
        return <div style={{ textAlign: 'right' }}>{this.formatCurrency(rowData[column.field])}</div>;
    }

    roundedNumberTemplate = (rowData, column) => {
        return <div style={{ textAlign: 'right' }}>{Math.round(rowData[column.field])}</div>;
    }

    renderSaveCancel = (columnSpread) => {
        return (
            <Grid item xs={columnSpread} style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }} >
                <Button variant='contained' color='default' style={{ marginRight: '15px', minWidth: '80px' }} onClick={this.onClickCancel} disabled={!this.state.isDirty}>Cancel</Button>
                <Button variant='contained' color='primary' onClick={this.onClickSave} style={{ minWidth: '80px' }} disabled={!this.state.isDirty && this.state.statusFilter !== CodeGroupMappingsFilter.NeedsReview}>{this.state.okButtonValue}</Button>
            </Grid>
        );
    }

    renderFilterAndSaveCancel = () => {
        let statusFilterOptions = [
            CodeGroupMappingsFilter.All,
            CodeGroupMappingsFilter.Confirmed,
            CodeGroupMappingsFilter.NotMapped,
            CodeGroupMappingsFilter.NeedsReview
        ];

        return (
            <Grid container spacing={24}>
                <Grid item xs={3}>
                    <FormControl fullWidth disabled={this.state.isDirty} >
                        <InputLabel htmlFor="statusFilter" style={{ fontWeight: 'normal' }}>Status</InputLabel>
                        <Select
                            value={this.state.statusFilter}
                            onChange={(e) => this.handleFilterChange(e, 'statusFilter')}
                            inputProps={{ name: 'statusFilter', id: 'statusFilter' }}
                        >
                            {statusFilterOptions.map((option) => {
                                return <MenuItem key={option} value={option}>{option}</MenuItem>;
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={3}>
                    <FormControl fullWidth disabled={this.state.isDirty} >
                        <InputLabel htmlFor="hospitalRevenueCategoryFilter" style={{ fontWeight: 'normal' }}>Hospital Code Category</InputLabel>
                        <Select
                            value={(this.state.hospitalRevenueCategoryFilter ? this.state.hospitalRevenueCategoryFilter : '')}
                            onChange={(e) => this.handleFilterChange(e, 'hospitalRevenueCategoryFilter')}
                            inputProps={{ name: 'hospitalRevenueCategoryFilter', id: 'hospitalRevenueCategoryFilter' }}
                        >
                            {this.state.loadingMappingDetails &&
                                <MenuItem key={this.state.hospitalRevenueCategoryFilter} value={this.state.hospitalRevenueCategoryFilter}>{this.state.hospitalRevenueCategoryFilter}</MenuItem>
                            }

                            {!this.state.loadingMappingDetails && <MenuItem key={null} value={null}>(All)</MenuItem>}
                            {!this.state.loadingMappingDetails &&
                                this.state.mappingsVM.hospitalRevenueCategories.map((option) => {
                                    return <MenuItem key={option} value={option}>{option}</MenuItem>;
                                })
                            }
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={3}>
                    <FormControl fullWidth disabled={this.state.isDirty} >
                        { /* <InputLabel htmlFor="descriptionSearchFilter" style={{ fontWeight: 'normal' }}>Hospital Code Description</InputLabel> */}
                        <TextField id='hospitalCodeDescriptionFilter' inputRef={(el) => this.hospitalCodeDescriptionFilter = el} label='Hospital Code Description' defaultValue={this.state.hospitalCodeDescriptionFilter} key={this.state.hospitalCodeDescriptionFilter}
                            fullWidth
                            onChange={() => { if (!this.state.isSearchDirty) this.setState({ isSearchDirty: true }); }}
                            // onBlur={(e) => { if (!this.state.hospitalCodeDescriptionFilter !== e.target.value) this.setState({ hospitalCodeDescriptionFilter: e.target.value }); }}
                            InputProps={{ // capital "I" is needed
                                maxLength: "50",
                                endAdornment:
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="search"
                                            onClick={() => this.setState({ hospitalCodeDescriptionFilter: this.hospitalCodeDescriptionFilter.value }, this.loadMappings)}
                                            disabled={!this.state.isSearchDirty}
                                        >
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                            }}
                        />
                    </FormControl>
                </Grid>
                {this.renderSaveCancel(3)}
                <Grid item xs={1} />
            </Grid>
        );
    }

    renderMappingDetails = () => {
        if (this.state.loadingMappingDetails)
            return <em>Loading...</em>;

        return (
            <div>
                {this.state.mappingsVM.mappings.length >= 50 && <em>50+ records found. Use filter to narrow your search.</em>}
                <DataTable ref={(el) => this.dt = el} value={this.state.mappingsVM.mappings} style={{ tableLayout: "auto" }} editable>
                    <Column field="mapping.hospitalRevenueCategory" header="Hospital Code Category" sortable="true" />
                    <Column field="mapping.hospitalCodeDescription" header="Hospital Code Description" sortable="true" />
                    <Column field="mapping.iVetCodeGroupId" header="Mapped Code" body={this.codeGroupEditor} sortable="true" />
                    <Column header="Additional Fields" body={this.additionalFieldsEditor} />
                    <Column field="trailingTwelveMonthDollar" header="TTM $" style={{ width: '12em' }} body={this.currencyTemplate} sortable="true" />
                    <Column field="trailingTwelveMonthQuantity" header="Quantity TTM" style={{ width: '12em' }} body={this.roundedNumberTemplate} sortable="true" />
                    <Column field="revenueYearToDate" header="YTD" style={{ width: '12em' }} body={this.currencyTemplate} sortable="true" />
                </DataTable>
                <br />
                {this.renderSaveCancel(12)}
            </div>
        );
    }

    render() {
        return (
            <div>
                <br />
                {this.renderMappingValidationTable()}
                <hr />
                {this.renderFilterAndSaveCancel(4)}
                <br />
                {this.renderMappingDetails()}
                <br />
                <br />
            </div>
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/Hospitals/CodeGroupMappings.js